import _extends from "/ecomm-marketplace/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
import _objectWithoutProperties from "/ecomm-marketplace/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["checked", "options", "onChange", "defaultValue", "fontSize", "width", "name", "hasSublabels", "hasSubDescriptions", "required", "showChecksWhenDisabled", "updateCurrentValueWithDefaultValue", "hasTooltips", "tooltipOptions"];
var __jsx = React.createElement;
import React, { useState, useEffect, forwardRef } from 'react';
import { RadioGroup, Radio } from 'react-radio-group';
import _ from 'lodash';
import { display, fontSize as fontSizeStyled, width as widthStyled, margin as marginStyled, space } from 'styled-system';
import styled from 'styled-components';
import useStyledTheme from 'shared/hooks/use-styled-theme';
import { Label } from 'shared/components/legacy-rebass';
import RadioCheckedIcon from 'shared/assets/radio-checked';
import RadioUncheckedIcon from 'shared/assets/radio-unchecked';
import RadioDisabledIcon from 'shared/assets/radio-disabled';
import Tooltip from 'shared/components/tooltip';
var StyledRadioGroup = styled(RadioGroup).withConfig({
  displayName: "radio-group__StyledRadioGroup",
  componentId: "sc-ldsof4-0"
})(["display:", ";"], function (_ref) {
  var hasTooltips = _ref.hasTooltips;
  return hasTooltips ? 'flex' : 'block';
});
var RadioLabel = styled(Label).withConfig({
  displayName: "radio-group__RadioLabel",
  componentId: "sc-ldsof4-1"
})(["", " ", " ", " ", " position:relative;line-height:16px;cursor:", ";padding-top:0;margin-bottom:", ";", " .--bold{font-weight:bold;margin-right:6px;}& > svg{position:absolute;left:0;top:0;}"], display, fontSizeStyled, widthStyled, marginStyled, function (_ref2) {
  var disabled = _ref2.disabled;
  return disabled && 'not-allowed' || 'pointer';
}, function (_ref3) {
  var marginBottom = _ref3.marginBottom;
  return marginBottom || '4px';
}, space);
var StyledRadio = styled(Radio).withConfig({
  displayName: "radio-group__StyledRadio",
  componentId: "sc-ldsof4-2"
})(["height:17px;width:17px;opacity:0;position:absolute;top:0;left:0;+ span{display:inline-block;}+ span,+ .radioDiv{color:#6d747b;padding-left:30px;height:var(--radio-height);width:100%;text-align:left;padding-top:", ";padding-bottom:", ";}&:disabled + span,&:disabled + .radioDiv{opacity:0.5;cursor:not-allowed;}&:checked:disabled + span,&:checked:disabled + .radioDiv{opacity:0.5;cursor:not-allowed;}&.focus-visible + span{box-shadow:0 0 0 2pt rgba(11,153,230,0.4);}"], function (props) {
  return props.fontSize ? "calc((16px - ".concat(props.fontSize, ") / 2)") : '0';
}, function (props) {
  return props.fontSize ? "calc((16px - ".concat(props.fontSize, ") / 2)") : '0';
});
export default /*#__PURE__*/forwardRef(function (/** @type { any } */props, ref) {
  var checked = props.checked,
    _props$options = props.options,
    options = _props$options === void 0 ? [] : _props$options,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? _.noop : _props$onChange,
    _props$defaultValue = props.defaultValue,
    defaultValue = _props$defaultValue === void 0 ? '' : _props$defaultValue,
    _props$fontSize = props.fontSize,
    fontSize = _props$fontSize === void 0 ? '14px' : _props$fontSize,
    width = props.width,
    name = props.name,
    hasSublabels = props.hasSublabels,
    hasSubDescriptions = props.hasSubDescriptions,
    _props$required = props.required,
    required = _props$required === void 0 ? false : _props$required,
    _props$showChecksWhen = props.showChecksWhenDisabled,
    showChecksWhenDisabled = _props$showChecksWhen === void 0 ? false : _props$showChecksWhen,
    _props$updateCurrentV = props.updateCurrentValueWithDefaultValue,
    updateCurrentValueWithDefaultValue = _props$updateCurrentV === void 0 ? false : _props$updateCurrentV,
    _props$hasTooltips = props.hasTooltips,
    hasTooltips = _props$hasTooltips === void 0 ? false : _props$hasTooltips,
    _props$tooltipOptions = props.tooltipOptions,
    tooltipOptions = _props$tooltipOptions === void 0 ? null : _props$tooltipOptions,
    otherProps = _objectWithoutProperties(props, _excluded);
  var theme = useStyledTheme();
  // keep track of the current value
  var _useState = useState(''),
    currentValue = _useState[0],
    setCurrentValue = _useState[1];
  function handleChange(value) {
    setCurrentValue(value);
    onChange(value);
  }
  useEffect(function () {
    if (!_.isNil(checked)) {
      // set currentValue to checked prop if one is provided
      setCurrentValue(checked);
    } else if (currentValue === '' && defaultValue !== '' && !_.isNil(defaultValue)) {
      // if we don't start with a default value but then get one, we should default to that
      setCurrentValue(defaultValue);
    } else if (updateCurrentValueWithDefaultValue && currentValue !== defaultValue) {
      // if updateCurrentValueWithDefaultValue is true, then defaultValue is allowed to change and currentValue should
      // reflect it
      setCurrentValue(defaultValue);
    }
  }, [checked, currentValue, defaultValue]);
  return __jsx(StyledRadioGroup, {
    name: name,
    ref: ref,
    onChange: handleChange,
    selectedValue: currentValue,
    role: "radiogroup",
    "aria-label": name,
    hasTooltips: hasTooltips
  }, _.map(options, function (option) {
    var _theme$customized$col, _theme$customized, _theme$colors;
    var label = option.label,
      sublabel = option.sublabel,
      subDescription = option.subDescription,
      value = option.value,
      disabled = option.disabled,
      LabelComponent = option.LabelComponent,
      _option$marginRight = option.marginRight,
      marginRight = _option$marginRight === void 0 ? '45px' : _option$marginRight,
      marginBottom = option.marginBottom;
    var tooltipCopy = tooltipOptions === null || tooltipOptions === void 0 ? void 0 : tooltipOptions[value];
    return __jsx(RadioLabel, _extends({
      mr: marginRight,
      key: value,
      fontSize: fontSize,
      width: option.width || width,
      mb: marginBottom
    }, otherProps), __jsx(StyledRadio, {
      name: "radio",
      value: value,
      disabled: disabled,
      fontSize: fontSize,
      "data-cy": option.radioButtonDataCyName,
      "data-test": option.radioButtonDataCyName,
      "data-testid": option.radioButtonDataCyName,
      "aria-checked": value === currentValue,
      role: "radio",
      required: required
    }), hasSublabels && !hasSubDescriptions && !LabelComponent && __jsx("span", null, __jsx("span", {
      className: "--bold",
      "data-cy": option.dataCyName || '',
      "data-test": option.dataCyName || '',
      "data-testid": option.dataCyName || ''
    }, label), __jsx("span", null, sublabel)), hasSublabels && hasSubDescriptions && !LabelComponent && __jsx(StyledSubDescriptionWrapper, null, __jsx("div", null, __jsx("span", {
      className: "--bold",
      "data-cy": option.dataCyName || '',
      "data-test": option.dataCyName || '',
      "data-testid": option.dataCyName || ''
    }, label), __jsx("span", null, sublabel)), __jsx("p", null, subDescription)), !hasSublabels && !LabelComponent && __jsx("span", null, label), LabelComponent && __jsx("div", {
      className: "radioDiv"
    }, __jsx(LabelComponent, null)), (!disabled || showChecksWhenDisabled) && value === currentValue && __jsx(RadioCheckedIcon, {
      color: disabled ? '#b7bec3' : (_theme$customized$col = theme === null || theme === void 0 || (_theme$customized = theme.customized) === null || _theme$customized === void 0 || (_theme$customized = _theme$customized.colors) === null || _theme$customized === void 0 ? void 0 : _theme$customized.buttonsLinks) !== null && _theme$customized$col !== void 0 ? _theme$customized$col : theme === null || theme === void 0 || (_theme$colors = theme.colors) === null || _theme$colors === void 0 ? void 0 : _theme$colors.primaryBlue
    }), !disabled && value !== currentValue && __jsx(RadioUncheckedIcon, null), disabled && value !== currentValue && __jsx(RadioDisabledIcon, {
      cursor: "not-allowed"
    }), hasTooltips && tooltipCopy && __jsx(Tooltip, {
      tooltipCopy: tooltipCopy,
      grey: true,
      left: "auto",
      bottom: "30px",
      right: "-61px",
      width: "239px"
    }));
  }));
});
var StyledSubDescriptionWrapper = styled.div.withConfig({
  displayName: "radio-group__StyledSubDescriptionWrapper",
  componentId: "sc-ldsof4-3"
})(["display:flex;flex-direction:column;margin-left:35px;gap:12px;"]);